/**
 * FolderBrowser — reusable, fully self-contained folder-browsing UI.
 *
 * Owns:
 *  - Search term + tag/source filters (compact navbar popover buttons)
 *  - View mode: "folders" (default) or "all files" flat list
 *  - Folder contents + all-files data fetching
 *  - All write mutations (create folder, rename, delete, move)
 *  - Drag-and-drop upload + ghost rows
 *  - Upload and create-folder modals
 *
 * Navigation is *controlled*: the parent owns `openFolderId` and `breadcrumb`
 * and responds to `onOpenFolder` / `onNavigateToFolder`.
 *
 * View mode can also be controlled (DocumentHubPage uses this to keep `?view=files`
 * in the URL).  If `viewMode` / `onViewModeChange` are omitted the mode is managed
 * as internal state.
 *
 * Used by:
 *  - DocumentHubPage  (URL-driven navigation + URL-driven view mode)
 *  - GroupDocumentsTab (useState-driven navigation, internal view mode)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { Link, useLocation } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { setDragHint } from 'src/lib/dragHint';
import { resolveTenantCode } from 'src/lib/tenantRouting';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  ArrowsClockwiseIcon,
  BuildingsIcon,
  CaretRightIcon,
  DotsThreeIcon,
  CloudIcon,
  ArrowsInIcon,
  MagnifyingGlassIcon,
  SpinnerIcon,
  UploadSimpleIcon,
  UsersThreeIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  LinkSimpleIcon,
  ArrowBendUpRightIcon,
  PlusIcon,
  FolderSimplePlusIcon
} from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';
import { Menu, MenuItem } from 'src/components/ui/Menu';
import Popover from 'src/components/ui/Popover';
import { twMerge } from 'tailwind-merge';
import { toast } from '@/components/ui/toast';
import Banner from 'src/components/ui/Banner';

import Button from 'src/components/ui/Button';
import Panel from 'src/components/ui/Panel';
import Loading from 'src/components/ui/Loading';
import ErrorMessage from 'src/components/ui/Error';
import { ConfirmModal } from 'src/components/ui/ConfirmModal';
import ShareModal from 'src/components/ui/ShareModal';
import useDocumentShare from 'src/features/wiki/hooks/useDocumentShare';
import { ResourcePermissionRole } from 'src/features/chat/types';
import BatchUploadModal from 'src/features/uploads/components/BatchUploadModal';
import useActiveTenantRole from 'src/hooks/useActiveTenantRole';

import { format } from 'date-fns';
import { getSharePointConnectionDetails, syncSharePointConnection } from 'src/features/database/api';
import WikiFilterDropdown from 'src/features/wiki/components/WikiFilterDropdown';
import TagPillSelect, { type TagPillSelectGroup } from 'src/features/groups/components/TagPillSelect';
import type { TagPillGroup } from 'src/features/tags/TagPill';
import SearchInputField from 'src/components/ui/SearchInput';
import { getFilterOptions } from '../api';
import * as api from '../api';
import {
  DocumentHubFilters,
  DocumentHubNode,
  FolderContentsResponse,
  isDraftDocumentType,
  isDraftsFolder,
  isOrganisationLibraryNode,
  isUserFolder,
  isWorkflowsFolderId,
  SOURCE_CATEGORY_LABELS
} from '../types';
import { useUploadBatchContext } from 'src/features/uploads/UploadBatchContext';
import type { DocumentActionReviewInfo } from 'src/features/groups/api';
import {
  statutoryDestructiveActionCopy,
  type StatutoryDestructiveAction
} from 'src/features/groups/components/StatutoryComplianceSummary';
import FolderContents from './FolderContents';
import DocumentMoveModal from './DocumentMoveModal';
import type { DocumentMoveDestination } from './DocumentMoveModal';
import DocumentPickerModal from 'src/components/DocumentPickerModal';
import { useGroups } from 'src/features/groups/hooks';
import { findNode } from '../tree';

type ViewMode = 'folders' | 'files';

const TREE_QUERY_KEY = ['document-hub', 'tree'];
const GROUP_VIRTUAL_ROOT = 'groups';
const GROUP_FOLDER_PREFIX = `${GROUP_VIRTUAL_ROOT}/`;

export interface FolderBrowserProps {
  // ── Controlled navigation ──────────────────────────────────────────────────
  /** Currently open folder (null = root of this browser). Controlled by parent. */
  openFolderId: string | null;
  /** Full ancestor path to the current folder, from the browser root downward. */
  breadcrumb: DocumentHubNode[];
  /** Parent calls this when user opens a folder row. */
  onOpenFolder: (node: DocumentHubNode) => void;
  /**
   * Parent calls this when the user navigates via breadcrumbs or the Up button.
   * `null` means "go to browser root".
   */
  onNavigateToFolder: (folderId: string | null) => void;

  // ── Controlled view mode (optional) ───────────────────────────────────────
  /**
   * When provided, view mode is controlled by the parent (DocumentHubPage uses
   * this to sync `?view=files` into the URL).  When omitted, view mode is
   * managed as internal state.
   */
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;

  // ── Controlled filters (optional) ─────────────────────────────────────────
  /** When provided, filters are controlled by the parent (e.g. synced to URL). */
  filters?: DocumentHubFilters;
  onFiltersChange?: (filters: DocumentHubFilters) => void;

  // ── Controlled search (optional) ──────────────────────────────────────────
  /** When provided, search is controlled by the parent (e.g. synced to URL). */
  search?: string;
  onSearchChange?: (term: string) => void;
  /** Optional single-shot handler that clears both filters and search in one update. */
  onClearAll?: () => void;

  // ── Capabilities ──────────────────────────────────────────────────────────
  /** Whether write actions (upload, create folder, rename, delete) are shown. */
  canWrite?: boolean;

  // ── Group config ──────────────────────────────────────────────────────────
  /**
   * If this browser is rooted inside a group folder, pass the group folder node
   * here so the group-sharing banner is shown.
   */
  groupFolder?: DocumentHubNode | null;
  /**
   * Absolute link target for the group name in the sharing banner.
   * E.g. `/acme/groups/01J...`
   */
  groupLink?: string;
  /**
   * The folder ID that acts as the "home" of this browser.
   * Clicking Home or Up from this folder is a no-op (null = hub root).
   */
  rootFolderId?: string | null;
  /** Label for the browser home crumb when `rootFolderId` is set. */
  rootFolderLabel?: string;
  /** When false, omit the leading Document Hub crumb (scoped browsers). */
  showHubHome?: boolean;
  /** Hide in-panel breadcrumbs (the page header already shows the trail). */
  hideTrail?: boolean;
  /**
   * Set while this browser is mounted so a page-header crumb can move a dragged
   * file or folder into a destination folder.
   */
  moveDroppedRef?: React.RefObject<((kind: 'document' | 'folder', id: string, destinationId: string) => void) | null>;
  /** When true, folder contents omit subfolders (files only). */
  filesOnly?: boolean;
  /**
   * Pre-populate the search term (e.g. when navigating from the home search bar).
   * Only used as the initial value — the user can clear/change it freely.
   */
  initialSearch?: string;
  /**
   * When true, forces the "all files" flat view, hides the folder/files view
   * toggle, and adds checkboxes to each row.  `onSelectionChange` is called
   * with the `document_id` values of the selected rows whenever the selection
   * changes.
   */
  multiselect?: boolean;
  /** When `multiselect` is true, whether more than one file can be selected. Defaults to true. */
  enableMultiRowSelection?: boolean;
  onSelectionChange?: (documentIds: string[], nodes?: DocumentHubNode[]) => void;
  /** Document-picker mode: minimal row actions in the file list. */
  pickerMode?: boolean;
  /** Hide these document ids from the all-files list (already linked). */
  excludeDocumentIds?: Set<string>;
  /** Show these document ids greyed-out and unselectable. */
  disabledDocumentIds?: Set<string>;
  disabledDocumentReason?: string;
  /** When set, only these document types are listed (and Drafts/Workflows folders are hidden). */
  allowedDocumentTypes?: Set<string>;
  /** When set, only folders of these types are listed. */
  allowedFolderTypes?: Set<string>;
  /**
   * At hub root, only show these folders (and inject them from the tree if
   * they sit under another space, e.g. a single group folder).
   */
  visibleRootFolderIds?: readonly string[];
  /** Hide the Drafts folder and canvas files. Workflows stay available. */
  omitDrafts?: boolean;
  /**
   * Adds extra bottom padding to the file list so there's room to scroll the
   * last rows/pagination above the fixed tasks button.
   */
  reserveUploadWidgetSpace?: boolean;
  /**
   * Optional lookup for pending action-import reviews on file rows.
   * When present, matching documents show an indicator and open with the
   * extraction query params so the wiki Action review tab is available.
   */
  getDocumentActionReview?: (documentId: string) => DocumentActionReviewInfo | null;
  /** When provided, files linked as statutory evidence show a subtitle and extra confirmation on destructive actions. */
  getStatutoryDocumentLabels?: (documentId: string) => readonly string[];
}

/** Collect all folder IDs that are descendants of (and including) a given root node. */
function collectFolderIds(node: DocumentHubNode): Set<string> {
  const ids = new Set<string>();
  function walk(n: DocumentHubNode) {
    ids.add(n.id);
    (n.children ?? []).forEach(walk);
  }
  walk(node);
  return ids;
}

function isOrgLibraryFile(node: DocumentHubNode): boolean {
  return isOrganisationLibraryNode(node);
}

const ORG_LIBRARY_DOCUMENT_TYPES = new Set(['tenant_file', 'wiki']);

const ORG_LIBRARY_DELETE_DISABLED_REASON =
  'You need organisation member access to delete files in the Organisation Folder.';
const ORG_LIBRARY_MOVE_DISABLED_REASON =
  'You need organisation member access to move files in the Organisation Folder.';
const ORG_LIBRARY_LINK_DISABLED_REASON =
  'You need organisation member access to link Organisation Folder files into a group.';

const ANALYSIS_IN_PROGRESS = new Set(['pending', 'extracting', 'tagging']);

function pageNeedsAnalysis(page: { items: { analysis_status?: string | null }[] }) {
  return page.items.some((item) => !!item.analysis_status && ANALYSIS_IN_PROGRESS.has(item.analysis_status));
}

function nextOffsetPage<T extends { items: unknown[]; total: number }>(lastPage: T, pages: T[]) {
  const loaded = pages.reduce((count, page) => count + page.items.length, 0);
  if (lastPage.items.length === 0 || loaded >= lastPage.total) return undefined;
  return loaded;
}

function replaceAnalysingPages<T extends { items: { analysis_status?: string | null }[] }>(
  current: InfiniteData<T, number> | undefined,
  updates: { pageParam: number; page: T }[]
): InfiniteData<T, number> | undefined {
  if (!current || updates.length === 0) return current;
  const pages = current.pages.slice();
  for (const update of updates) {
    const index = current.pageParams.indexOf(update.pageParam);
    if (index >= 0) pages[index] = update.page;
  }
  return { ...current, pages };
}

export default function FolderBrowser({
  openFolderId,
  breadcrumb,
  onOpenFolder,
  onNavigateToFolder,
  viewMode: externalViewMode,
  onViewModeChange,
  filters: externalFilters,
  onFiltersChange,
  search: externalSearch,
  onSearchChange,
  onClearAll,
  canWrite = true,
  groupFolder = null,
  groupLink,
  rootFolderId = null,
  rootFolderLabel,
  showHubHome = true,
  hideTrail = false,
  moveDroppedRef,
  filesOnly = false,
  initialSearch = '',
  multiselect = false,
  enableMultiRowSelection = true,
  onSelectionChange,
  pickerMode = false,
  excludeDocumentIds,
  disabledDocumentIds,
  disabledDocumentReason,
  allowedDocumentTypes,
  allowedFolderTypes,
  visibleRootFolderIds,
  omitDrafts = false,
  getDocumentActionReview,
  getStatutoryDocumentLabels,
  reserveUploadWidgetSpace = false
}: FolderBrowserProps) {
  const navigate = useTenantNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const location = useLocation();

  const wikiPathForDocument = useCallback(
    (documentId: string) => {
      const review = getDocumentActionReview?.(documentId);
      if (!review) return `/wiki/${documentId}`;
      const params = new URLSearchParams({ extraction: review.extractionId });
      if (review.groupId) params.set('group', review.groupId);
      const tenantCode = resolveTenantCode(location.pathname);
      const tenantPathPrefix = tenantCode ? `/${tenantCode}` : '';
      const returnTo = `${location.pathname.startsWith(tenantPathPrefix) ? location.pathname.slice(tenantPathPrefix.length) || '/' : location.pathname}${location.search}`;
      params.set('return_to', returnTo);
      return `/wiki/${documentId}?${params.toString()}`;
    },
    [getDocumentActionReview, location.pathname, location.search]
  );

  // ── Search / filters / view mode ──────────────────────────────────────────
  const [internalSearch, setInternalSearch] = useState(externalSearch ?? initialSearch);
  const searchTerm = externalSearch ?? internalSearch;
  // Keep internal search in sync when initialSearch changes (home page search bar).
  useEffect(() => {
    if (externalSearch === undefined) setInternalSearch(initialSearch);
  }, [initialSearch, externalSearch]);

  // Debounced search term used in query keys so we don't fire a request on every keystroke.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);
  const [internalFilters, setInternalFilters] = useState<DocumentHubFilters>({
    tags: [],
    source: []
  });
  const filters = externalFilters ?? internalFilters;
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>(multiselect ? 'files' : 'folders');
  const viewMode = externalViewMode ?? internalViewMode;
  const [sorting, setSorting] = useState<SortingState>([{ id: 'updated_at', desc: true }]);
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setSorting((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);
  const sortBy: 'name' | 'updated_at' = sorting[0]?.id === 'name' ? 'name' : 'updated_at';
  const sortDesc = sorting[0]?.desc ?? true;
  const PAGE_SIZE = 40;
  const setViewMode = useCallback(
    (mode: ViewMode) => {
      // Reset search, filters and pagination so stale state from one view doesn't bleed into the other
      if (onSearchChange) onSearchChange('');
      else setInternalSearch('');
      if (!onFiltersChange) setInternalFilters({ tags: [], source: [] });
      if (onViewModeChange) onViewModeChange(mode);
      else setInternalViewMode(mode);
    },
    [onViewModeChange]
  );
  const handleFilterChange = useCallback(
    (key: keyof DocumentHubFilters, value: string[]) => {
      const next = { ...filters, [key]: value };
      if (onFiltersChange) {
        onFiltersChange(next);
      } else {
        setInternalFilters(next);
      }
    },
    [filters, onFiltersChange]
  );

  const handleSearchChange = useCallback(
    (term: string) => {
      if (onSearchChange) {
        onSearchChange(term);
      } else {
        setInternalSearch(term);
      }
    },
    [onSearchChange]
  );

  // ── Compact filter bar state ───────────────────────────────────────────────
  const [searchExpanded, setSearchExpanded] = useState(!!(externalSearch ?? initialSearch));
  const [filterAnchor, setFilterAnchor] = useState<{ el: HTMLElement; key: keyof DocumentHubFilters } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: filterOptions } = useQuery({
    queryKey: ['document-hub', 'filter-options', openFolderId ?? null],
    queryFn: () => getFilterOptions(openFolderId),
    staleTime: 60_000
  });
  const tagFilterGroups = useMemo(
    (): TagPillSelectGroup[] =>
      Object.entries(
        (filterOptions?.resource_tags ?? []).reduce<Record<string, NonNullable<DocumentHubNode['tags']>>>(
          (groups, tag) => {
            (groups[tag.group] ??= []).push(tag);
            return groups;
          },
          {}
        )
      ).map(([group, tags]) => ({
        slug: group as TagPillGroup,
        label:
          {
            document_type: 'Document type',
            domain: 'Domain',
            site: 'Site',
            national_returns: 'National Returns'
          }[group] ?? group,
        cardinality: 'multiple',
        options: tags.map((tag) => ({
          id: `${tag.group}:${tag.slug}`,
          label: tag.label,
          group: tag.group as TagPillGroup
        }))
      })),
    [filterOptions]
  );
  const selectedTagFilterIds = filters.tags;
  const omitDraftsAndWorkflows = !!allowedDocumentTypes && !allowedFolderTypes;
  const sourceOptions = useMemo(() => {
    const entries = Object.entries(SOURCE_CATEGORY_LABELS);
    const visible = omitDraftsAndWorkflows ? entries.filter(([value]) => value !== 'workflows') : entries;
    return visible.map(([value, label]) => ({ value, label }));
  }, [omitDraftsAndWorkflows]);

  // ── Upload ────────────────────────────────────────────────────────────────
  const { startBackgroundUpload } = useUploadBatchContext();

  // ── Modal / DnD state ─────────────────────────────────────────────────────
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const canCreateFolders = useFeatureFlag('document-hub-folders') === true;
  useEffect(() => {
    setCreatingFolder(false);
  }, [openFolderId]);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [trailMenuAnchor, setTrailMenuAnchor] = useState<HTMLElement | null>(null);
  const addMenuAnchorRef = useRef<HTMLDivElement>(null);
  const [addExistingPicker, setAddExistingPicker] = useState<'link' | 'move' | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // ── Refresh state ─────────────────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['document-hub', 'contents'] }),
      queryClient.invalidateQueries({ queryKey: ['document-hub', 'all-files'] }),
      new Promise((resolve) => setTimeout(resolve, 500))
    ]).finally(() => setIsRefreshing(false));
  }, [queryClient]);

  // ── Tree (for path labels in all-files view) ──────────────────────────────
  const { data: tree } = useQuery({
    queryKey: TREE_QUERY_KEY,
    queryFn: api.getDocumentTree,
    refetchOnMount: 'always'
  });

  // ── Data ──────────────────────────────────────────────────────────────────
  const invalidateContents = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['document-hub', 'contents'] });
    queryClient.invalidateQueries({ queryKey: ['document-hub', 'all-files'] });
    queryClient.invalidateQueries({ queryKey: ['document-hub', 'group-file-ids'] });
    queryClient.invalidateQueries({ queryKey: TREE_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['document-hub', 'location'] });
  }, [queryClient]);

  const hubDocumentTypes = allowedDocumentTypes ? [...allowedDocumentTypes] : undefined;
  const folderTypes = allowedFolderTypes ? [...allowedFolderTypes] : undefined;
  const excludedDocumentIds = excludeDocumentIds?.size ? [...excludeDocumentIds] : undefined;

  const {
    data: contentsData,
    isLoading: contentsLoading,
    isError: contentsError,
    isFetchNextPageError: contentsNextPageError,
    isRefetchError: contentsRefetchError,
    hasNextPage: contentsHasNextPage,
    isFetchingNextPage: contentsFetchingNextPage,
    fetchNextPage: fetchNextContents,
    refetch: refetchContents
  } = useInfiniteQuery({
    queryKey: [
      'document-hub',
      'contents',
      openFolderId,
      debouncedSearchTerm,
      filters,
      filesOnly,
      hubDocumentTypes,
      folderTypes,
      excludedDocumentIds,
      omitDraftsAndWorkflows,
      sortBy,
      sortDesc
    ],
    queryFn: ({ pageParam }) =>
      api.getFolderContents(openFolderId, {
        search: debouncedSearchTerm || undefined,
        offset: pageParam,
        limit: PAGE_SIZE,
        tag: filters.tags.length ? filters.tags : undefined,
        files_only: filesOnly || undefined,
        hub_document_type: hubDocumentTypes,
        folder_type: folderTypes,
        exclude_document_id: excludedDocumentIds,
        omit_drafts_and_workflows: omitDraftsAndWorkflows || undefined,
        sort_by: sortBy,
        sort_desc: sortDesc
      }),
    initialPageParam: 0,
    getNextPageParam: nextOffsetPage,
    enabled: viewMode === 'folders'
  });

  const movableSource = omitDraftsAndWorkflows ? filters.source.filter((s) => s !== 'workflows') : filters.source;
  const allFilesSource =
    omitDraftsAndWorkflows && movableSource.length === 0 ? ['library', 'organisation', 'groups'] : movableSource;

  const {
    data: allFilesData,
    isLoading: allFilesLoading,
    hasNextPage: allFilesHasNextPage,
    isFetchingNextPage: allFilesFetchingNextPage,
    isFetchNextPageError: allFilesNextPageError,
    isRefetchError: allFilesRefetchError,
    fetchNextPage: fetchNextAllFiles,
    refetch: refetchAllFiles
  } = useInfiniteQuery({
    queryKey: [
      'document-hub',
      'all-files',
      debouncedSearchTerm,
      filters,
      allFilesSource,
      hubDocumentTypes,
      excludedDocumentIds,
      sortBy,
      sortDesc
    ],
    queryFn: ({ pageParam }) =>
      api.getAllFiles({
        search: debouncedSearchTerm || undefined,
        offset: pageParam,
        limit: PAGE_SIZE,
        tag: filters.tags.length ? filters.tags : undefined,
        source: allFilesSource.length ? allFilesSource : undefined,
        hub_document_type: hubDocumentTypes,
        exclude_document_id: excludedDocumentIds,
        sort_by: sortBy,
        sort_desc: sortDesc
      }),
    initialPageParam: 0,
    getNextPageParam: nextOffsetPage,
    enabled: viewMode === 'files',
    placeholderData: (previousData) => previousData
  });

  const contentsNeedsAnalysis = contentsData?.pages.some(pageNeedsAnalysis) ?? false;
  useEffect(() => {
    if (viewMode !== 'folders' || !contentsNeedsAnalysis) return;
    const queryKey = [
      'document-hub',
      'contents',
      openFolderId,
      debouncedSearchTerm,
      filters,
      filesOnly,
      hubDocumentTypes,
      folderTypes,
      excludedDocumentIds,
      omitDraftsAndWorkflows,
      sortBy,
      sortDesc
    ] as const;
    const refresh = async () => {
      const current =
        queryClient.getQueryData<InfiniteData<{ items: { analysis_status?: string | null }[] }, number>>(queryKey);
      if (!current) return;
      const settled = await Promise.allSettled(
        current.pages.flatMap((page, index) => {
          if (!pageNeedsAnalysis(page)) return [];
          const pageParam = current.pageParams[index];
          return [
            api
              .getFolderContents(openFolderId, {
                search: debouncedSearchTerm || undefined,
                offset: pageParam,
                limit: PAGE_SIZE,
                tag: filters.tags.length ? filters.tags : undefined,
                files_only: filesOnly || undefined,
                hub_document_type: hubDocumentTypes,
                folder_type: folderTypes,
                exclude_document_id: excludedDocumentIds,
                omit_drafts_and_workflows: omitDraftsAndWorkflows || undefined,
                sort_by: sortBy,
                sort_desc: sortDesc
              })
              .then((fresh) => ({ pageParam, page: fresh }))
          ];
        })
      );
      const updates = settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      queryClient.setQueryData<InfiniteData<Awaited<ReturnType<typeof api.getFolderContents>>, number>>(
        queryKey,
        (existing) => replaceAnalysingPages(existing, updates)
      );
    };
    const id = window.setInterval(() => void refresh(), 5_000);
    return () => window.clearInterval(id);
  }, [
    viewMode,
    contentsNeedsAnalysis,
    queryClient,
    openFolderId,
    debouncedSearchTerm,
    filters,
    filesOnly,
    hubDocumentTypes,
    folderTypes,
    excludedDocumentIds,
    omitDraftsAndWorkflows,
    sortBy,
    sortDesc
  ]);

  const allFilesNeedsAnalysis = allFilesData?.pages.some(pageNeedsAnalysis) ?? false;
  useEffect(() => {
    if (viewMode !== 'files' || !allFilesNeedsAnalysis) return;
    const queryKey = [
      'document-hub',
      'all-files',
      debouncedSearchTerm,
      filters,
      allFilesSource,
      hubDocumentTypes,
      excludedDocumentIds,
      sortBy,
      sortDesc
    ] as const;
    const refresh = async () => {
      const current =
        queryClient.getQueryData<InfiniteData<{ items: { analysis_status?: string | null }[] }, number>>(queryKey);
      if (!current) return;
      const settled = await Promise.allSettled(
        current.pages.flatMap((page, index) => {
          if (!pageNeedsAnalysis(page)) return [];
          const pageParam = current.pageParams[index];
          return [
            api
              .getAllFiles({
                search: debouncedSearchTerm || undefined,
                offset: pageParam,
                limit: PAGE_SIZE,
                tag: filters.tags.length ? filters.tags : undefined,
                source: allFilesSource.length ? allFilesSource : undefined,
                hub_document_type: hubDocumentTypes,
                exclude_document_id: excludedDocumentIds,
                sort_by: sortBy,
                sort_desc: sortDesc
              })
              .then((fresh) => ({ pageParam, page: fresh }))
          ];
        })
      );
      const updates = settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      queryClient.setQueryData<InfiniteData<Awaited<ReturnType<typeof api.getAllFiles>>, number>>(
        queryKey,
        (existing) => replaceAnalysingPages(existing, updates)
      );
    };
    const id = window.setInterval(() => void refresh(), 5_000);
    return () => window.clearInterval(id);
  }, [
    viewMode,
    allFilesNeedsAnalysis,
    queryClient,
    debouncedSearchTerm,
    filters,
    allFilesSource,
    hubDocumentTypes,
    excludedDocumentIds,
    sortBy,
    sortDesc
  ]);

  // IDs of all folders in this browser's scope (used to scope "all files" view
  // when rootFolderId is set, e.g. when embedded in a group page).
  const scopedFolderIds = useMemo<Set<string> | null>(() => {
    if (!tree) return null;
    if (rootFolderId) {
      const rootNode = findNode(tree, rootFolderId);
      return rootNode ? collectFolderIds(rootNode) : null;
    }
    if (visibleRootFolderIds && visibleRootFolderIds.length > 0) {
      const ids = new Set<string>();
      for (const id of visibleRootFolderIds) {
        const node = findNode(tree, id);
        if (node) collectFolderIds(node).forEach((folderId) => ids.add(folderId));
      }
      return ids.size > 0 ? ids : null;
    }
    return null;
  }, [rootFolderId, tree, visibleRootFolderIds]);

  const folderContents: DocumentHubNode[] = useMemo(() => {
    let items = contentsData?.pages.flatMap((page) => page.items) ?? [];
    if (omitDrafts) {
      items = items.filter((n) => !isDraftsFolder(n) && !isDraftDocumentType(n.document_type));
    }
    const atHubRoot = openFolderId === null && !rootFolderId;
    if (visibleRootFolderIds && visibleRootFolderIds.length > 0 && atHubRoot && tree) {
      const allowed = new Set(visibleRootFolderIds);
      const fromApi = items.filter((n) => n.type === 'folder' && allowed.has(n.id));
      const have = new Set(fromApi.map((n) => n.id));
      const extras: DocumentHubNode[] = [];
      for (const id of visibleRootFolderIds) {
        if (have.has(id)) continue;
        const node = findNode(tree, id);
        if (node) extras.push({ ...node, children: null });
      }
      return [...fromApi, ...extras];
    }
    if (visibleRootFolderIds && scopedFolderIds && items.some((n) => n.type === 'file')) {
      return items.filter((n) => {
        if (n.type === 'folder') return true;
        return !n.parent_id || scopedFolderIds.has(n.parent_id);
      });
    }
    return items;
  }, [contentsData, omitDrafts, openFolderId, rootFolderId, scopedFolderIds, tree, visibleRootFolderIds]);

  // Scope filter is still client-side (only applies in group context)
  const allFileItems: DocumentHubNode[] = useMemo(() => {
    return (allFilesData?.pages.flatMap((page) => page.items) ?? []).filter((n) => {
      if (omitDrafts && isDraftDocumentType(n.document_type)) return false;
      if (scopedFolderIds && n.parent_id && !scopedFolderIds.has(n.parent_id)) return false;
      return true;
    });
  }, [allFilesData, omitDrafts, scopedFolderIds]);

  // Group-scoped all-files is filtered client-side. A page of global results can
  // contain no scoped files, so FolderContents never mounts an end sentinel and
  // later matches never load. Keep requesting pages until a scoped row appears.
  useEffect(() => {
    if (viewMode !== 'files') return;
    if (!scopedFolderIds) return;
    if (allFileItems.length > 0) return;
    if (!allFilesHasNextPage || allFilesFetchingNextPage || allFilesNextPageError) return;
    void fetchNextAllFiles();
  }, [
    allFileItems.length,
    allFilesNextPageError,
    allFilesFetchingNextPage,
    allFilesHasNextPage,
    fetchNextAllFiles,
    scopedFolderIds,
    viewMode
  ]);

  const allFilesTotal = allFilesData?.pages.at(-1)?.total ?? 0;

  const isAtVirtualRoot = openFolderId === null && !rootFolderId;
  const isInGroupFolder = !!openFolderId?.startsWith(GROUP_FOLDER_PREFIX);
  const currentGroupId = isInGroupFolder ? (openFolderId!.split('/').pop() ?? null) : null;
  const isInSharedFolder = !!openFolderId?.startsWith('organisation');
  const isInWorkflowsFolder = isWorkflowsFolderId(openFolderId);

  const WORKFLOW_EMPTY_MESSAGES: Record<string, string> = {
    'feature-files': 'Files generated or uploaded by CompliMind features appear here',
    'feature-files/chat': 'Files you upload in Chat conversations will appear here',
    'feature-files/writings': 'Files you upload while using the Writing feature will appear here',
    'feature-files/asset-review-uploads': 'Files uploaded during Asset Reviews will appear here',
    'feature-files/review-uploads': 'Documents uploaded to the Review Assistant will appear here',
    'feature-files/actions': 'Files from Actions will appear here',
    'feature-files/training-hub': 'Documents from Training Hub will appear here',
    'feature-files/meetings': 'Meeting documents will appear here',
    'feature-files/pam-feedback': 'PAM feedback documents will appear here'
  };
  const workflowEmptyHint = isInWorkflowsFolder
    ? (openFolderId ? WORKFLOW_EMPTY_MESSAGES[openFolderId] : undefined) || 'Files from this feature appear here'
    : undefined;
  const isCurrentFolderReadOnly =
    isAtVirtualRoot || (breadcrumb.length > 0 ? !!breadcrumb[breadcrumb.length - 1].read_only : false);
  // Workflow folders do not accept hub uploads (files come from the feature),
  // but Move / Share / Delete stay available for the holder.
  const uploadsDisabled = isCurrentFolderReadOnly || isInWorkflowsFolder;
  const isGroupViewer = isInGroupFolder && (!canWrite || !!groupFolder?.read_only || isCurrentFolderReadOnly);
  const groupViewerEditDisabledReason = isGroupViewer
    ? "You don't have permission to edit files in this group"
    : undefined;

  // ── SharePoint banner ──────────────────────────────────────────────────────
  // The SP folder ID is "organisation/sharepoint/{conn_id}[/sub/path]".
  // Extract the connection ID so we can fetch last_synced_at and trigger sync.
  const SP_VIRTUAL_ROOT = 'organisation/sharepoint';
  const isInSharePointFolder = !!openFolderId?.startsWith(SP_VIRTUAL_ROOT + '/');
  const spConnectionId = isInSharePointFolder ? openFolderId!.slice(SP_VIRTUAL_ROOT.length + 1).split('/')[0] : null;

  type SPStatus = { last_synced_at: string | null; syncInProgress: boolean; syncStartedAt: string | null };
  const { data: spStatus } = useQuery<SPStatus>({
    queryKey: ['sp-connection-details', spConnectionId],
    queryFn: async () => {
      const res = await getSharePointConnectionDetails(spConnectionId!);
      const docs: Array<{ last_synced_at?: string; updated_at?: string; ingestion_status?: string }> =
        res?.documents ?? [];

      const earliest = docs
        .map((d) => (d.last_synced_at ? new Date(d.last_synced_at).getTime() : Infinity))
        .reduce((a, b) => Math.min(a, b), Infinity);
      const last_synced_at = isFinite(earliest) ? new Date(earliest).toISOString() : null;

      const processingDocs = docs.filter(
        (d) => d.ingestion_status === 'pending' || d.ingestion_status === 'in_progress'
      );
      const syncInProgress = processingDocs.length > 0;
      const startTimes = processingDocs
        .map((d) => (d.updated_at ? new Date(d.updated_at).getTime() : null))
        .filter((t): t is number => t !== null);
      const syncStartedAt = startTimes.length > 0 ? new Date(Math.min(...startTimes)).toISOString() : null;

      return { last_synced_at, syncInProgress, syncStartedAt };
    },
    enabled: isInSharePointFolder && !!spConnectionId,
    staleTime: 30_000,
    refetchOnMount: 'always' as const,
    refetchInterval: (query) => (query.state.data?.syncInProgress ? 5_000 : false)
  });

  const spSyncMutation = useMutation({
    mutationFn: () => syncSharePointConnection(spConnectionId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sp-connection-details', spConnectionId] });
      toast.success('SharePoint sync started');
    },
    onError: () => toast.error('Failed to start sync')
  });

  // Refresh folder contents when a running sync transitions to complete
  const prevSyncInProgress = useRef(spStatus?.syncInProgress);
  useEffect(() => {
    const was = prevSyncInProgress.current;
    const is = spStatus?.syncInProgress;
    prevSyncInProgress.current = is;
    if (was && !is) invalidateContents();
  }, [spStatus?.syncInProgress]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: ({ nodeId }: { nodeId: string }) => api.deleteNode(nodeId),
    onSuccess: () => {
      invalidateContents();
      toast.success('Deleted');
    },
    onError: () => toast.error('Failed to delete')
  });

  // ── Share picker state + tenant role ─────────────────────────────────────
  const [sharingNode, setSharingNode] = useState<DocumentHubNode | null>(null);
  const [movingNode, setMovingNode] = useState<DocumentHubNode | null>(null);
  const [pendingStatutoryAction, setPendingStatutoryAction] = useState<{
    kind: StatutoryDestructiveAction;
    node: DocumentHubNode;
    labels: readonly string[];
  } | null>(null);
  const [linkingNode, setLinkingNode] = useState<DocumentHubNode | null>(null);
  const { data: groupsList } = useGroups();
  const { role: tenantRole, isStandardMember } = useActiveTenantRole();
  const canShareWithOrg = isStandardMember;
  const canManageSP = tenantRole === 'champion' || tenantRole === 'admin';
  const organisationName = user?.getActiveTenant(location.pathname)?.name ?? undefined;

  const {
    existingShares,
    groupShares,
    tenantShare,
    handleAddPeople,
    handleRemovePerson,
    handleUpdatePermission,
    handleRemoveGroupShare
  } = useDocumentShare({
    externalId: sharingNode?.document_id ?? undefined,
    isOwner: !!sharingNode?.document_id
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const canEditNodeGroup = useCallback(
    (node: DocumentHubNode) => {
      if (currentGroupId && !isGroupViewer) return true;
      const groupId = node.group_id ?? currentGroupId;
      const groupPerm = groupsList?.groups.find((g) => g.id === groupId)?.user_permission ?? 0;
      return groupPerm >= ResourcePermissionRole.WRITE;
    },
    [currentGroupId, groupsList?.groups, isGroupViewer]
  );

  const isPlacedInGroup = useCallback(
    (node: DocumentHubNode) => {
      return node.group_relation === 'owned' || node.group_relation === 'linked' || !!currentGroupId;
    },
    [currentGroupId]
  );

  const canDeleteNode = useCallback(
    (node: DocumentHubNode) => {
      if (isOrgLibraryFile(node)) return isStandardMember;
      if (isPlacedInGroup(node)) return canEditNodeGroup(node);
      return true;
    },
    [canEditNodeGroup, isPlacedInGroup, isStandardMember]
  );

  const canMoveNode = useCallback(
    (node: DocumentHubNode) => {
      if (node.type !== 'file' || !node.document_id) return false;
      if (isDraftDocumentType(node.document_type)) return false;

      if (isPlacedInGroup(node)) {
        if (isOrgLibraryFile(node) && node.group_relation !== 'owned') {
          return isStandardMember;
        }
        if (node.group_relation === 'owned') {
          return canEditNodeGroup(node);
        }
        const holderId = node.personal_folder_user_id ?? node.created_by;
        return holderId === user?.user_id;
      }

      if (isOrgLibraryFile(node)) {
        return isStandardMember;
      }
      return true;
    },
    [canEditNodeGroup, isPlacedInGroup, isStandardMember, user?.user_id]
  );

  const deleteDisabledReason = useCallback(
    (node: DocumentHubNode) =>
      isOrgLibraryFile(node) && !canDeleteNode(node) ? ORG_LIBRARY_DELETE_DISABLED_REASON : '',
    [canDeleteNode]
  );

  const moveDisabledReason = useCallback(
    (node: DocumentHubNode) => {
      if (canMoveNode(node)) return '';
      if (isDraftDocumentType(node.document_type)) {
        return "Drafts can't be moved to a group or the Organisation Folder.";
      }
      if (isPlacedInGroup(node)) {
        if (isOrgLibraryFile(node) && node.group_relation !== 'owned') {
          return ORG_LIBRARY_MOVE_DISABLED_REASON;
        }
        return "You don't have permission to edit files in this group";
      }
      if (isOrgLibraryFile(node)) return ORG_LIBRARY_MOVE_DISABLED_REASON;
      return "This file can't be moved.";
    },
    [canMoveNode, isPlacedInGroup]
  );

  const statutoryLabelsForNode = useCallback(
    (node: DocumentHubNode) => {
      if (!node.document_id || !getStatutoryDocumentLabels) return [];
      return getStatutoryDocumentLabels(node.document_id);
    },
    [getStatutoryDocumentLabels]
  );

  const confirmStatutoryAction = useCallback(
    (kind: StatutoryDestructiveAction, node: DocumentHubNode, proceed: () => void) => {
      const labels = statutoryLabelsForNode(node);
      if (labels.length === 0) {
        proceed();
        return;
      }
      setPendingStatutoryAction({ kind, node, labels });
    },
    [statutoryLabelsForNode]
  );

  const performDelete = useCallback(
    (node: DocumentHubNode) => {
      deleteMutation.mutate({ nodeId: node.id });
    },
    [deleteMutation]
  );

  const performUnlink = useCallback(
    (node: DocumentHubNode) => {
      if (!node.document_id || !currentGroupId) return;
      api
        .unshareFromGroup(node.document_id, currentGroupId)
        .then(() => {
          invalidateContents();
          toast.success('Unlinked');
        })
        .catch((err: { response?: { data?: { detail?: string } } }) =>
          toast.error(err?.response?.data?.detail ?? 'Failed to unlink')
        );
    },
    [currentGroupId, invalidateContents]
  );

  const handleDelete = useCallback(
    (node: DocumentHubNode) => {
      if (!canDeleteNode(node)) return;
      confirmStatutoryAction('delete', node, () => {
        if (window.confirm(`Delete "${node.name}"? This cannot be undone.`)) {
          performDelete(node);
        }
      });
    },
    [canDeleteNode, confirmStatutoryAction, performDelete]
  );

  const handleShareWith = useCallback((node: DocumentHubNode) => {
    setSharingNode(node);
  }, []);

  const handleMove = useCallback(
    (node: DocumentHubNode) => {
      if (isUserFolder(node)) {
        setMovingNode(node);
        return;
      }
      if (!canMoveNode(node)) return;
      confirmStatutoryAction('move', node, () => setMovingNode(node));
    },
    [canMoveNode, confirmStatutoryAction]
  );

  const renameMutation = useMutation({
    mutationFn: ({ documentId, filename }: { documentId: string; filename: string }) =>
      api.renameDocumentFilename(documentId, filename),
    onMutate: async ({ documentId, filename }) => {
      const previousContents = queryClient.getQueriesData<InfiniteData<FolderContentsResponse>>({
        queryKey: ['document-hub', 'contents']
      });
      const previousFiles = queryClient.getQueriesData<InfiniteData<FolderContentsResponse>>({
        queryKey: ['document-hub', 'all-files']
      });
      const patch = (data: InfiniteData<FolderContentsResponse> | undefined) => {
        if (!data?.pages) return data;
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((node) => {
              if (node.document_id !== documentId) return node;
              const dot = node.name.lastIndexOf('.');
              const extension = dot > 0 ? node.name.slice(dot) : '';
              return { ...node, name: `${filename}${extension}` };
            })
          }))
        };
      };
      // Apply before the first await so the row shows the new name as the editor closes.
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'contents'] },
        patch
      );
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'all-files'] },
        patch
      );
      await queryClient.cancelQueries({ queryKey: ['document-hub', 'contents'] });
      await queryClient.cancelQueries({ queryKey: ['document-hub', 'all-files'] });
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'contents'] },
        patch
      );
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'all-files'] },
        patch
      );
      return { previousContents, previousFiles };
    },
    onError: (error: { response?: { data?: { detail?: string } } }, _vars, context) => {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to rename file');
      context?.previousContents.forEach(([key, data]) => queryClient.setQueryData(key, data));
      context?.previousFiles.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-hub', 'contents'] });
      queryClient.invalidateQueries({ queryKey: ['document-hub', 'all-files'] });
      queryClient.invalidateQueries({ queryKey: ['document-hub', 'recent'] });
      toast.success('File renamed', { autoClose: 2000 });
    }
  });

  const canLinkNode = useCallback(
    (node: DocumentHubNode) => isOrgLibraryFile(node) && isStandardMember,
    [isStandardMember]
  );

  const linkDisabledReason = useCallback(
    (node: DocumentHubNode) => (isOrgLibraryFile(node) && !canLinkNode(node) ? ORG_LIBRARY_LINK_DISABLED_REASON : ''),
    [canLinkNode]
  );

  const handleLinkToGroup = useCallback(
    (node: DocumentHubNode) => {
      if (!canLinkNode(node)) return;
      setLinkingNode(node);
    },
    [canLinkNode]
  );

  const toastMovedTo = useCallback(
    (label: string, to: string) => {
      toast.success(
        <span>
          Moved to{' '}
          <button
            type="button"
            className="font-medium underline underline-offset-2 hover:text-blue-800"
            onClick={(e) => {
              e.stopPropagation();
              navigate(to);
            }}
          >
            {label}
          </button>
        </span>
      );
    },
    [navigate]
  );

  const moveFolderMutation = useMutation({
    mutationFn: ({ folderId, parentId }: { folderId: string; parentId: string }) =>
      api.moveUserFolder(folderId, parentId),
    onMutate: ({ folderId }) => {
      const previousContents = queryClient.getQueriesData<InfiniteData<FolderContentsResponse>>({
        queryKey: ['document-hub', 'contents']
      });
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'contents'] },
        (data) => {
          if (!data?.pages) return data;
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.filter((item) => item.id !== folderId)
            }))
          };
        }
      );
      return { previousContents };
    },
    onSuccess: () => {
      invalidateContents();
      toast.success('Moved');
    },
    onError: (err: { response?: { data?: { detail?: string } } }, _vars, context) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to move folder');
      context?.previousContents.forEach(([key, data]) => queryClient.setQueryData(key, data));
    }
  });

  const fileInFolderMutation = useMutation({
    mutationFn: ({ documentId, folderId }: { documentId: string; folderId: string }) =>
      api.fileDocumentsInFolder(folderId, [documentId]),
    onSuccess: () => {
      invalidateContents();
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to move file');
    }
  });

  useEffect(() => {
    if (!moveDroppedRef) return;
    moveDroppedRef.current = pickerMode
      ? null
      : (kind, id, destinationId) => {
          if (kind === 'folder') {
            if (id === destinationId) return;
            moveFolderMutation.mutate({ folderId: id, parentId: destinationId });
            return;
          }
          fileInFolderMutation.mutate({ documentId: id, folderId: destinationId });
        };
    return () => {
      moveDroppedRef.current = null;
    };
  }, [fileInFolderMutation, moveDroppedRef, moveFolderMutation, pickerMode]);

  const renameFolderMutation = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) => api.renameUserFolder(folderId, name),
    onMutate: async ({ folderId, name }) => {
      const previousContents = queryClient.getQueriesData<InfiniteData<FolderContentsResponse>>({
        queryKey: ['document-hub', 'contents']
      });
      const previousTree = queryClient.getQueryData<DocumentHubNode[]>(TREE_QUERY_KEY);
      const renameNode = (node: DocumentHubNode): DocumentHubNode => (node.id === folderId ? { ...node, name } : node);
      // Apply before the first await so the row shows the new name as the editor closes.
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'contents'] },
        (data) => {
          if (!data?.pages) return data;
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map(renameNode)
            }))
          };
        }
      );
      const renameInTree = (nodes: DocumentHubNode[]): DocumentHubNode[] =>
        nodes.map((node) => ({
          ...renameNode(node),
          children: node.children ? renameInTree(node.children) : node.children
        }));
      queryClient.setQueryData<DocumentHubNode[]>(TREE_QUERY_KEY, (data) => (data ? renameInTree(data) : data));
      await queryClient.cancelQueries({ queryKey: ['document-hub', 'contents'] });
      await queryClient.cancelQueries({ queryKey: TREE_QUERY_KEY });
      queryClient.setQueriesData<InfiniteData<FolderContentsResponse>>(
        { queryKey: ['document-hub', 'contents'] },
        (data) => {
          if (!data?.pages) return data;
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map(renameNode)
            }))
          };
        }
      );
      queryClient.setQueryData<DocumentHubNode[]>(TREE_QUERY_KEY, (data) => (data ? renameInTree(data) : data));
      return { previousContents, previousTree };
    },
    onSuccess: () => {
      invalidateContents();
      toast.success('Folder renamed');
    },
    onError: (err: { response?: { data?: { detail?: string } } }, _vars, context) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to rename folder');
      context?.previousContents.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      queryClient.setQueryData(TREE_QUERY_KEY, context?.previousTree);
    }
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => api.deleteUserFolder(folderId),
    onSuccess: () => {
      invalidateContents();
      toast.success('Folder deleted');
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to delete folder');
    }
  });

  const handleDeleteFolder = useCallback(
    (node: DocumentHubNode) => {
      void api.countFilesInFolder(node.id).then(
        (count) => {
          const files = count === 1 ? 'the 1 file inside it' : `the ${count} files inside it`;
          const message =
            count === 0
              ? `Delete folder "${node.name}"? This cannot be undone.`
              : `Delete folder "${node.name}" and ${files}? This cannot be undone.`;
          if (window.confirm(message)) {
            deleteFolderMutation.mutate(node.id);
          }
        },
        () => {
          toast.error('Failed to delete folder');
        }
      );
    },
    [deleteFolderMutation]
  );

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!openFolderId) return;
      await api.createUserFolder(name.trim(), openFolderId);
    },
    onSuccess: () => {
      invalidateContents();
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.trim() ? detail : 'Failed to create folder');
    }
  });

  const relocateMutation = useMutation({
    mutationFn: async ({
      documentId,
      folderId,
      destination
    }: {
      documentId?: string;
      folderId?: string;
      destination: DocumentMoveDestination;
    }) => {
      if (folderId) {
        if (destination.kind === 'group') {
          await api.relocateUserFolder(folderId, 'group', destination.groupId);
          return;
        }
        await api.relocateUserFolder(folderId, destination.kind);
        return;
      }
      if (!documentId) return;
      if (destination.kind === 'group') {
        await api.relocateDocument(documentId, 'group', destination.groupId);
        return;
      }
      await api.relocateDocument(documentId, destination.kind);
    },
    onSuccess: (_data, vars) => {
      setMovingNode(null);
      invalidateContents();
      const destination = vars.destination;
      if (destination.kind === 'personal') {
        toastMovedTo('Personal Folder', '/documents?folder=my-documents');
        return;
      }
      if (destination.kind === 'organisation') {
        toastMovedTo('Organisation Folder', '/documents?folder=organisation');
        return;
      }
      const groupName = groupsList?.groups.find((g) => g.id === destination.groupId)?.name;
      toastMovedTo(groupName?.trim() || 'the group', `/groups/${destination.groupId}/files`);
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      const looksLikeId = typeof detail === 'string' && !/\s/.test(detail.trim());
      toast.error(typeof detail === 'string' && detail.trim() && !looksLikeId ? detail : 'Failed to move file');
    }
  });

  const handleUnshare = useCallback(
    (node: DocumentHubNode) => {
      if (!node.document_id || !currentGroupId || node.group_relation !== 'linked') return;
      confirmStatutoryAction('unlink', node, () => {
        if (window.confirm(`Unlink "${node.name}" from this group? The file stays in the Organisation Folder.`)) {
          performUnlink(node);
        }
      });
    },
    [confirmStatutoryAction, currentGroupId, performUnlink]
  );

  const handlePendingStatutoryConfirm = useCallback(() => {
    if (!pendingStatutoryAction) return;
    const { kind, node } = pendingStatutoryAction;
    setPendingStatutoryAction(null);
    if (kind === 'delete') {
      performDelete(node);
      return;
    }
    if (kind === 'unlink') {
      performUnlink(node);
      return;
    }
    setMovingNode(node);
  }, [pendingStatutoryAction, performDelete, performUnlink]);

  const pendingStatutoryCopy = pendingStatutoryAction
    ? statutoryDestructiveActionCopy(
        pendingStatutoryAction.kind,
        pendingStatutoryAction.node.name,
        pendingStatutoryAction.labels
      )
    : null;

  const linkOrgLibraryMutation = useMutation({
    mutationFn: ({ documentIds }: { documentIds: string[] }) => {
      if (!currentGroupId) return Promise.reject(new Error('No group selected'));
      return api.linkOrganisationLibraryDocuments(currentGroupId, documentIds);
    },
    onSuccess: () => {
      setAddExistingPicker(null);
      invalidateContents();
      toast.success('Linked from Organisation Folder');
    },
    onError: (err: { response?: { data?: { detail?: string } } }) =>
      toast.error(err?.response?.data?.detail ?? 'Failed to link files')
  });

  const linkToGroupMutation = useMutation({
    mutationFn: ({ documentId, groupId }: { documentId: string; groupId: string }) =>
      api.linkOrganisationLibraryDocuments(groupId, [documentId]),
    onSuccess: (_data, vars) => {
      setLinkingNode(null);
      invalidateContents();
      const groupName = groupsList?.groups.find((g) => g.id === vars.groupId)?.name;
      toast.success(
        <span>
          Linked to{' '}
          <button
            type="button"
            className="font-medium underline underline-offset-2 hover:text-blue-800"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/groups/${vars.groupId}/files`);
            }}
          >
            {groupName?.trim() || 'the group'}
          </button>
        </span>
      );
    },
    onError: (err: { response?: { data?: { detail?: string } } }) =>
      toast.error(err?.response?.data?.detail ?? 'Failed to link file')
  });

  const moveToGroupMutation = useMutation({
    mutationFn: ({ documentIds }: { documentIds: string[] }) => {
      if (!currentGroupId) return Promise.reject(new Error('No group selected'));
      return api.moveDocumentsToGroup(currentGroupId, documentIds);
    },
    onSuccess: () => {
      setAddExistingPicker(null);
      invalidateContents();
      if (currentGroupId) {
        toastMovedTo(groupFolder?.name?.trim() || 'the group', `/groups/${currentGroupId}/files`);
      } else {
        toast.success('Moved to this group');
      }
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      const looksLikeId = typeof detail === 'string' && !/\s/.test(detail.trim());
      toast.error(typeof detail === 'string' && detail.trim() && !looksLikeId ? detail : 'Failed to move files');
    }
  });

  const {
    data: groupFolderFileIds,
    isSuccess: groupFilesSuccess,
    isFetching: groupFilesFetching,
    isError: groupFilesError,
    refetch: refetchGroupFiles
  } = useQuery({
    queryKey: ['document-hub', 'group-file-ids', currentGroupId],
    queryFn: async () => {
      const groupFolderId = `${GROUP_FOLDER_PREFIX}${currentGroupId}`;
      const linked: string[] = [];
      const owned: string[] = [];
      let offset = 0;
      const limit = 200;
      while (true) {
        const page = await api.getFolderContents(groupFolderId, { files_only: true, offset, limit });
        for (const node of page.items) {
          if (!node.document_id) continue;
          linked.push(node.document_id);
          if (node.group_relation === 'owned') owned.push(node.document_id);
        }
        offset += page.items.length;
        if (offset >= page.total || page.items.length === 0) break;
      }
      return { linked, owned };
    },
    enabled: !!addExistingPicker && !!currentGroupId,
    refetchOnMount: 'always'
  });

  const groupFilesReady = groupFilesSuccess && !groupFilesFetching && !groupFilesError;

  const alreadyLinkedDocumentIds = groupFolderFileIds?.linked ?? [];
  const alreadyOwnedDocumentIds = groupFolderFileIds?.owned ?? [];

  const handleDroppedFiles = useCallback(
    (files: FileList | File[]) => {
      if (!canWrite) return;
      if (viewMode === 'folders' && uploadsDisabled) return;
      // In the flat all-files view uploads always go to Library ('my-documents').
      const targetFolder = viewMode === 'files' ? 'my-documents' : (openFolderId ?? null);
      const documentType = targetFolder === 'organisation' ? 'tenant_file' : undefined;
      const groupId = targetFolder?.startsWith(GROUP_FOLDER_PREFIX)
        ? targetFolder.slice(GROUP_FOLDER_PREFIX.length)
        : undefined;
      startBackgroundUpload(Array.from(files), { documentType, groupId }, () => {
        queryClient.invalidateQueries({ queryKey: ['document-hub', 'contents'] });
        queryClient.invalidateQueries({ queryKey: ['document-hub', 'all-files'] });
        queryClient.invalidateQueries({ queryKey: TREE_QUERY_KEY });
      });
    },
    [canWrite, uploadsDisabled, openFolderId, viewMode, startBackgroundUpload, queryClient]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      const canDrop = viewMode === 'files' || !uploadsDisabled;
      if (canDrop && e.dataTransfer.files.length > 0) handleDroppedFiles(e.dataTransfer.files);
    },
    [uploadsDisabled, viewMode, handleDroppedFiles]
  );

  // ── Breadcrumb ─────────────────────────────────────────────────────────────
  const atRoot = openFolderId === rootFolderId || openFolderId === null;
  const openFolderName = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].name : undefined;

  const handleUpward = useCallback(() => {
    if (atRoot) return;
    const parentCrumb = breadcrumb[breadcrumb.length - 2];
    onNavigateToFolder(parentCrumb?.id ?? rootFolderId);
  }, [atRoot, breadcrumb, rootFolderId, onNavigateToFolder]);

  // ── Render ────────────────────────────────────────────────────────────────
  const folderContextBanners = (
    <>
      {isInSharedFolder && !isInGroupFolder && !isInSharePointFolder && viewMode === 'folders' && (
        <Banner icon={BuildingsIcon}>
          This is {organisationName ?? 'your organisation'}'s shared folder. Documents uploaded here are visible to all
          members of {organisationName ?? 'your organisation'}.
        </Banner>
      )}
      {groupFolder && viewMode === 'folders' && (
        <Banner icon={UsersThreeIcon}>
          Files shown here either belong to this group or are linked from the organisational library. Visible to members
          of{'\u00A0'}
          {groupLink ? (
            <Link to={groupLink} className="font-medium underline underline-offset-2 hover:text-blue-900">
              {groupFolder.name}
            </Link>
          ) : (
            <span className="font-medium">{groupFolder.name}.</span>
          )}
        </Banner>
      )}
      {isInSharePointFolder && (
        <Banner
          icon={spStatus?.syncInProgress ? SpinnerIcon : CloudIcon}
          iconProps={spStatus?.syncInProgress ? { className: 'shrink-0 animate-spin' } : undefined}
          cta={
            canManageSP
              ? {
                  label: (
                    <span className="flex items-center gap-1">
                      <ArrowsClockwiseIcon size={12} className={spSyncMutation.isPending ? 'animate-spin' : ''} />
                      Sync now
                    </span>
                  ),
                  onClick: () => spSyncMutation.mutate(),
                  disabled: spSyncMutation.isPending || !!spStatus?.syncInProgress
                }
              : undefined
          }
        >
          <span className="truncate">
            {spStatus?.syncInProgress ? (
              <>
                Sync in progress
                {spStatus.syncStartedAt && (
                  <>
                    {' '}
                    — started at{' '}
                    <span className="font-medium">
                      {format(new Date(spStatus.syncStartedAt), 'MMM dd, yyyy HH:mm')}
                    </span>
                  </>
                )}
              </>
            ) : spStatus?.last_synced_at ? (
              <>
                Last synced from SharePoint at{' '}
                <span className="font-medium">{format(new Date(spStatus.last_synced_at), 'MMM dd, yyyy HH:mm')}</span>
              </>
            ) : (
              'Synced hourly from SharePoint'
            )}
          </span>
        </Banner>
      )}
    </>
  );

  const trail = useMemo(() => {
    const items: { key: string; label: string; onSelect?: () => void; destinationId?: string }[] = [];
    if (showHubHome) {
      items.push({ key: 'hub', label: 'Document Hub', onSelect: () => onNavigateToFolder(null) });
    }
    if (rootFolderLabel && rootFolderId) {
      items.push({
        key: `root-${rootFolderId}`,
        label: rootFolderLabel,
        destinationId: rootFolderId,
        onSelect: showHubHome ? () => onNavigateToFolder(rootFolderId) : undefined
      });
    }
    breadcrumb.forEach((crumb) => {
      items.push({
        key: crumb.id,
        label: crumb.name,
        destinationId: crumb.id,
        onSelect: () => onNavigateToFolder(crumb.id)
      });
    });
    if (items.length > 0) {
      const last = items.length - 1;
      items[last] = { key: items[last].key, label: items[last].label };
    }
    return items;
  }, [breadcrumb, onNavigateToFolder, rootFolderId, rootFolderLabel, showHubHome]);
  const trailTail = 3;
  const trailCollapsed = trail.length > trailTail + 1;
  const hiddenTrail = trailCollapsed ? trail.slice(1, -trailTail) : [];
  const visibleTrail = trailCollapsed ? [trail[0], ...trail.slice(-trailTail)] : trail;

  const browser = (
    <div className={twMerge('flex flex-col gap-2 w-full', 'min-h-0')}>
      {/* Navbar row — full width */}
      <div className="flex min-w-0 items-center gap-2 shrink-0">
        {hideTrail ? (
          <div className="flex min-w-0 flex-1 items-center">
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowUpIcon}
              onClick={handleUpward}
              title="Up"
              aria-label="Up one folder"
              disabled={atRoot}
            />
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowsClockwiseIcon}
              onClick={handleRefresh}
              title="Refresh"
              aria-label="Refresh folder"
              iconProps={{ className: isRefreshing ? 'animate-spin' : undefined }}
            />
          </div>
        ) : viewMode === 'folders' ? (
          /* Breadcrumb nav */
          <nav className="flex items-center gap-1 flex-1 min-w-0 text-sm">
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowUpIcon}
              onClick={handleUpward}
              title="Up"
              aria-label="Up one folder"
              disabled={atRoot}
            />
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowsClockwiseIcon}
              onClick={handleRefresh}
              title="Refresh"
              aria-label="Refresh folder"
              iconProps={{ className: isRefreshing ? 'animate-spin' : undefined }}
            />
            {visibleTrail.map((item, index) => {
              const isLast = index === visibleTrail.length - 1;
              const parentKey = trail.length >= 2 ? trail[trail.length - 2].key : null;
              const canDropHere =
                !pickerMode &&
                canWrite &&
                item.key === parentKey &&
                !!item.destinationId &&
                item.destinationId !== openFolderId;
              const crumbClass = twMerge(
                'px-1 truncate min-w-0 rounded',
                isLast ? 'text-content-default font-medium' : 'text-content-subtlest'
              );
              const dropHandlers = canDropHere
                ? {
                    onDragOver: (event: React.DragEvent) => {
                      if (event.dataTransfer.types.includes('Files')) return;
                      const hubDrag =
                        event.dataTransfer.types.includes('application/x-hub-folder') ||
                        event.dataTransfer.types.includes('application/x-hub-document');
                      if (!hubDrag) return;
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      setDragHint(`Move to ${item.label}`);
                    },
                    onDragLeave: (event: React.DragEvent) => {
                      if (event.currentTarget.contains(event.relatedTarget as Node)) return;
                      setDragHint(null);
                    },
                    onDrop: (event: React.DragEvent) => {
                      event.preventDefault();
                      setDragHint(null);
                      const folderId = event.dataTransfer.getData('application/x-hub-folder');
                      const documentId = folderId ? '' : event.dataTransfer.getData('application/x-hub-document');
                      const destinationId = item.destinationId;
                      if (!destinationId) return;
                      if (folderId) {
                        if (folderId !== destinationId) {
                          moveFolderMutation.mutate({ folderId, parentId: destinationId });
                        }
                        return;
                      }
                      if (documentId) fileInFolderMutation.mutate({ documentId, folderId: destinationId });
                    }
                  }
                : {};
              return (
                <span key={item.key} className="flex items-center gap-1 min-w-0">
                  {index > 0 && <CaretRightIcon size={11} className="text-content-subtlest shrink-0" />}
                  {index === 1 && trailCollapsed && (
                    <>
                      <button
                        type="button"
                        className="inline-flex size-5 shrink-0 items-center justify-center rounded-md text-content-subtlest hover:bg-surface-hover hover:text-content-default"
                        aria-label="Show hidden folders"
                        aria-expanded={trailMenuAnchor != null}
                        onClick={(event) => setTrailMenuAnchor(event.currentTarget)}
                      >
                        <DotsThreeIcon size={16} weight="bold" />
                      </button>
                      <CaretRightIcon size={11} className="text-content-subtlest shrink-0" />
                    </>
                  )}
                  {item.onSelect ? (
                    <button
                      type="button"
                      onClick={item.onSelect}
                      className={twMerge(crumbClass, 'transition-colors hover:text-gray-900')}
                      {...dropHandlers}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className={crumbClass} {...dropHandlers}>
                      {item.label}
                    </span>
                  )}
                </span>
              );
            })}
            <Menu
              anchorEl={trailMenuAnchor}
              open={trailMenuAnchor != null}
              onClose={() => setTrailMenuAnchor(null)}
              placement="bottom-start"
            >
              {hiddenTrail.map((item) => (
                <MenuItem
                  key={item.key}
                  onClick={() => {
                    setTrailMenuAnchor(null);
                    item.onSelect?.();
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </nav>
        ) : (
          <nav className="flex items-center gap-1 flex-1 min-w-0 text-sm">
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowLeftIcon}
              onClick={() => setViewMode('folders')}
              title="Back"
              aria-label="Back"
            />
            <Button
              variant="ghost"
              size="sm"
              icon={ArrowsClockwiseIcon}
              onClick={handleRefresh}
              title="Refresh"
              aria-label="Refresh"
              iconProps={{ className: isRefreshing ? 'animate-spin' : undefined }}
            />
            <span className="flex items-center gap-1 min-w-0">
              <CaretRightIcon size={11} className="text-content-subtlest shrink-0" />
              <span className="text-content-default font-medium">
                All files{allFilesTotal > 0 && ` (${allFilesTotal})`}
              </span>
            </span>
          </nav>
        )}

        {/* ── Compact filter bar ── */}
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
          {/* Clear all filters */}
          {(filters.tags.length > 0 || filters.source.length > 0 || !!searchTerm) && (
            <button
              type="button"
              onClick={() => {
                if (onClearAll) {
                  onClearAll();
                } else {
                  const cleared = { tags: [], source: [] };
                  if (onFiltersChange) onFiltersChange(cleared);
                  else setInternalFilters(cleared);
                  handleSearchChange('');
                }
                setSearchExpanded(false);
              }}
              className="text-sm text-misc-link underline underline-offset-2 hover:opacity-75 transition-opacity whitespace-nowrap mr-2"
            >
              Clear filters
            </button>
          )}

          {/* Search */}
          {searchExpanded ? (
            <SearchInputField
              ref={searchInputRef}
              autoFocus
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search files…"
              className="h-9 w-44 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  handleSearchChange('');
                  setSearchExpanded(false);
                }
              }}
              onBlur={(e) => {
                if (!e.target.value) setSearchExpanded(false);
              }}
            />
          ) : (
            <Button
              variant={searchTerm ? 'primary' : 'ghost'}
              size="md"
              icon={MagnifyingGlassIcon}
              onClick={() => setSearchExpanded(true)}
            />
          )}

          <TagPillSelect
            groups={tagFilterGroups}
            value={selectedTagFilterIds}
            onChange={(ids) => {
              const next = { ...filters, tags: ids };
              if (onFiltersChange) onFiltersChange(next);
              else setInternalFilters(next);
            }}
            placeholder="Filter tags"
            ariaLabel="Filter tags"
            className="h-9 w-64"
            size="sm"
            singleLine
          />

          {/* Spaces filter — list view only */}
          {viewMode === 'files' && (
            <Button
              variant={filters.source.length ? 'primary' : 'ghost'}
              size="md"
              icon={ArrowsInIcon}
              onClick={(e) => setFilterAnchor({ el: e.currentTarget, key: 'source' })}
            >
              Folders{filters.source.length ? ` (${filters.source.length})` : ''}
            </Button>
          )}
        </div>

        {/* Filter popover */}
        <Popover
          open={!!filterAnchor}
          anchorEl={filterAnchor?.el ?? null}
          onClose={() => setFilterAnchor(null)}
          placement="bottom-start"
          className="mt-1 pt-3 min-w-[200px]"
        >
          {filterAnchor?.key === 'source' && (
            <WikiFilterDropdown
              bare
              options={sourceOptions}
              value={filters.source}
              onChange={(v) => handleFilterChange('source', v)}
              label="Folders"
              searchable={false}
              initialOpen={true}
            />
          )}
        </Popover>

        {/* Action buttons */}
        {!pickerMode &&
          (viewMode === 'files'
            ? canWrite && (
                <Tooltip title="Files will be saved to your Library" placement="bottom">
                  <span>
                    <Button
                      variant="primary"
                      size="md"
                      icon={UploadSimpleIcon}
                      className="max-md:[&_[data-slot=button-label]]:sr-only"
                      onClick={() => setUploadModalOpen(true)}
                    >
                      Upload to Library
                    </Button>
                  </span>
                </Tooltip>
              )
            : (canWrite || isInGroupFolder) && (
                <div className="flex items-center gap-2 shrink-0">
                  {canCreateFolders &&
                    canWrite &&
                    !isGroupViewer &&
                    (openFolderId === 'my-documents' ||
                      openFolderId === 'organisation' ||
                      openFolderId?.startsWith('groups/') ||
                      openFolderId?.startsWith('uf/')) && (
                      <Button
                        size="md"
                        variant="outline"
                        icon={FolderSimplePlusIcon}
                        className="max-md:[&_[data-slot=button-label]]:sr-only"
                        onClick={() => setCreatingFolder(true)}
                      >
                        New folder
                      </Button>
                    )}
                  {isInGroupFolder ? (
                    <>
                      <div className="relative inline-block flex-shrink-0" ref={addMenuAnchorRef}>
                        <Tooltip title={groupViewerEditDisabledReason ?? ''} placement="bottom">
                          <span>
                            <Button
                              size="md"
                              variant="primary"
                              icon={PlusIcon}
                              aria-haspopup="menu"
                              aria-expanded={addMenuOpen}
                              disabled={isGroupViewer}
                              className="max-md:[&_[data-slot=button-label]]:sr-only"
                              onClick={() => setAddMenuOpen(true)}
                            >
                              Add files
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                      <Menu
                        open={addMenuOpen}
                        anchorEl={addMenuAnchorRef.current}
                        onClose={() => setAddMenuOpen(false)}
                        placement="bottom-end"
                        className="w-80"
                      >
                        <MenuItem
                          icon={UploadSimpleIcon}
                          description="Upload new files into this group."
                          disabled={isCurrentFolderReadOnly}
                          onClick={() => {
                            setAddMenuOpen(false);
                            setUploadModalOpen(true);
                          }}
                        >
                          Upload files
                        </MenuItem>
                        <MenuItem
                          icon={LinkSimpleIcon}
                          description="The file stays in the Organisation Folder but is visible here."
                          onClick={() => {
                            setAddMenuOpen(false);
                            setAddExistingPicker('link');
                          }}
                        >
                          Link from Organisation Folder
                        </MenuItem>
                        <MenuItem
                          icon={ArrowBendUpRightIcon}
                          description="This group becomes the owner of the file."
                          onClick={() => {
                            setAddMenuOpen(false);
                            setAddExistingPicker('move');
                          }}
                        >
                          Move from Document Hub
                        </MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <Tooltip
                      title={
                        isAtVirtualRoot
                          ? 'Open the Library folder to upload personal documents'
                          : uploadsDisabled
                            ? isInWorkflowsFolder
                              ? 'Upload from the feature that created these files'
                              : 'This folder is read only — you cannot upload files here'
                            : ''
                      }
                      placement="bottom"
                    >
                      <span>
                        <Button
                          variant="primary"
                          size="md"
                          icon={UploadSimpleIcon}
                          className="max-md:[&_[data-slot=button-label]]:sr-only"
                          onClick={() => setUploadModalOpen(true)}
                          disabled={uploadsDisabled}
                        >
                          Upload Files
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              ))}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-2">
        {!hideTrail && folderContextBanners}

        {/* File list */}
        <div
          className={pickerMode ? 'relative' : 'relative'}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          {viewMode === 'files' ? (
            <FolderContents
              key="all-files"
              nodes={allFileItems}
              isLoading={allFilesLoading}
              onSelect={(node) => {
                if (!multiselect && node.type === 'file' && node.document_id) {
                  navigate(wikiPathForDocument(node.document_id), { state: { from: 'document-hub' } });
                }
              }}
              onDelete={handleDelete}
              onUnshare={isInGroupFolder ? handleUnshare : undefined}
              onMove={multiselect || pickerMode || isInSharePointFolder ? undefined : handleMove}
              onSaveFilename={
                multiselect || pickerMode || isInSharePointFolder
                  ? undefined
                  : (node, filename) => {
                      if (!node.document_id) return;
                      return renameMutation.mutateAsync({ documentId: node.document_id, filename });
                    }
              }
              onLinkToGroup={multiselect || pickerMode || isInSharePointFolder ? undefined : handleLinkToGroup}
              onShareWith={multiselect ? undefined : handleShareWith}
              canShareNode={(node) => node.document_type !== 'tenant_file' || canShareWithOrg}
              canDeleteNode={canDeleteNode}
              deleteDisabledReason={deleteDisabledReason}
              canMoveNode={canMoveNode}
              moveDisabledReason={moveDisabledReason}
              canLinkNode={canLinkNode}
              linkDisabledReason={linkDisabledReason}
              writeDisabledReason={groupViewerEditDisabledReason}
              folderReadOnly={false}
              showCreatedBy={!multiselect}
              preserveOrder
              manualSorting
              sorting={sorting}
              onSortingChange={handleSortingChange}
              infinite
              onReachEnd={allFilesHasNextPage && !allFilesFetchingNextPage ? () => fetchNextAllFiles() : undefined}
              reachEndError={allFilesNextPageError}
              onRetry={() => fetchNextAllFiles()}
              refetchError={allFilesRefetchError}
              onRefetch={() => refetchAllFiles()}
              isLoadingMore={allFilesFetchingNextPage}
              multiselect={multiselect}
              enableMultiRowSelection={enableMultiRowSelection}
              pickerMode={pickerMode}
              onSelectionChange={onSelectionChange}
              disabledDocumentIds={disabledDocumentIds}
              disabledDocumentReason={disabledDocumentReason}
              getDocumentActionReview={getDocumentActionReview}
              getStatutoryDocumentLabels={getStatutoryDocumentLabels}
            />
          ) : contentsLoading && !contentsData ? (
            <Loading text="Loading…" />
          ) : contentsError && !contentsData ? (
            <ErrorMessage size="sm" message="Failed to load folder contents" />
          ) : (
            <FolderContents
              key={`folder-${openFolderId ?? 'root'}`}
              nodes={folderContents}
              isLoading={contentsLoading}
              onSelect={(node) => {
                if (omitDrafts && (isDraftsFolder(node) || isDraftDocumentType(node.document_type))) {
                  return;
                }
                if (node.type === 'folder') {
                  onOpenFolder(node);
                  return;
                }
                if (pickerMode) return;
                if (node.document_id) {
                  navigate(wikiPathForDocument(node.document_id), { state: { from: 'document-hub' } });
                }
              }}
              onOpenFolder={
                omitDrafts
                  ? (node) => {
                      if (isDraftsFolder(node)) return;
                      onOpenFolder(node);
                    }
                  : onOpenFolder
              }
              onDelete={handleDelete}
              onDeleteFolder={pickerMode ? undefined : handleDeleteFolder}
              onUnshare={isInGroupFolder ? handleUnshare : undefined}
              onMove={pickerMode || isInSharePointFolder ? undefined : handleMove}
              onSaveFilename={
                pickerMode || isInSharePointFolder
                  ? undefined
                  : (node, filename) => {
                      if (!node.document_id) return;
                      return renameMutation.mutateAsync({ documentId: node.document_id, filename });
                    }
              }
              onSaveFolderName={
                pickerMode ? undefined : (node, name) => renameFolderMutation.mutateAsync({ folderId: node.id, name })
              }
              creatingFolder={canCreateFolders && creatingFolder}
              onCreateFolder={(name) => createFolderMutation.mutateAsync(name)}
              onCancelCreateFolder={() => setCreatingFolder(false)}
              onLinkToGroup={pickerMode || isInSharePointFolder ? undefined : handleLinkToGroup}
              onShareWith={handleShareWith}
              canShareNode={(node) => node.document_type !== 'tenant_file' || canShareWithOrg}
              canDeleteNode={canDeleteNode}
              deleteDisabledReason={deleteDisabledReason}
              canMoveNode={canMoveNode}
              moveDisabledReason={moveDisabledReason}
              canLinkNode={canLinkNode}
              linkDisabledReason={linkDisabledReason}
              writeDisabledReason={groupViewerEditDisabledReason}
              folderReadOnly={isCurrentFolderReadOnly && !isInWorkflowsFolder}
              showCreatedBy={isInSharedFolder}
              preserveOrder
              manualSorting
              sorting={sorting}
              onSortingChange={handleSortingChange}
              infinite
              onReachEnd={contentsHasNextPage && !contentsFetchingNextPage ? () => fetchNextContents() : undefined}
              reachEndError={contentsNextPageError}
              onRetry={() => fetchNextContents()}
              refetchError={contentsRefetchError}
              onRefetch={() => refetchContents()}
              isLoadingMore={contentsFetchingNextPage}
              multiselect={multiselect}
              enableMultiRowSelection={enableMultiRowSelection}
              pickerMode={pickerMode}
              onSelectionChange={onSelectionChange}
              disabledDocumentIds={disabledDocumentIds}
              disabledDocumentReason={disabledDocumentReason}
              getDocumentActionReview={getDocumentActionReview}
              getStatutoryDocumentLabels={getStatutoryDocumentLabels}
              onMoveToFolder={
                multiselect || pickerMode || !!groupViewerEditDisabledReason
                  ? undefined
                  : (documentId, folderId) => fileInFolderMutation.mutate({ documentId, folderId })
              }
              onMoveFolder={
                multiselect || pickerMode || !!groupViewerEditDisabledReason
                  ? undefined
                  : (folderId, parentId) => moveFolderMutation.mutate({ folderId, parentId })
              }
              emptyMessage={
                workflowEmptyHint || openFolderId === GROUP_VIRTUAL_ROOT ? (
                  <div className="flex flex-col items-center gap-1 py-4 text-content-subtlest text-sm">
                    <span>This folder is empty</span>
                    <span>
                      {openFolderId === GROUP_VIRTUAL_ROOT
                        ? 'Groups you belong to will appear here'
                        : workflowEmptyHint}
                    </span>
                  </div>
                ) : undefined
              }
            />
          )}

          {isDragOver && (viewMode === 'files' || !uploadsDisabled) && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-blue-50/90 border-2 border-dashed border-secondary-blue rounded-lg pointer-events-none">
              <UploadSimpleIcon size={40} className="text-secondary-blue" weight="light" />
              <p className="text-sm font-medium text-secondary-blue">
                {viewMode === 'files'
                  ? 'Drop files to add to your Library'
                  : `Drop files to upload${openFolderName ? ` into "${openFolderName}"` : ''}`}
              </p>
            </div>
          )}
          {isDragOver && viewMode === 'folders' && uploadsDisabled && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-gray-50/90 border-2 border-dashed border-gray-300 rounded-lg pointer-events-none">
              <p className="text-sm font-medium text-gray-500">
                {isInWorkflowsFolder ? 'Upload from the feature that created these files' : 'This folder is read only'}
              </p>
            </div>
          )}
        </div>
      </div>
      {/* end content */}

      {/* Modals */}
      {(() => {
        const folderId = viewMode === 'files' ? 'my-documents' : (openFolderId ?? null);
        const uploadDocumentType = folderId === 'organisation' ? 'tenant_file' : undefined;
        const uploadGroupId = folderId?.startsWith(GROUP_FOLDER_PREFIX)
          ? folderId.slice(GROUP_FOLDER_PREFIX.length)
          : undefined;
        return (
          <BatchUploadModal
            open={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            onComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['document-hub', 'contents'] });
              queryClient.invalidateQueries({ queryKey: ['document-hub', 'all-files'] });
              queryClient.invalidateQueries({ queryKey: TREE_QUERY_KEY });
              setUploadModalOpen(false);
            }}
            title={
              viewMode === 'files'
                ? 'Upload to Library'
                : groupFolder
                  ? `Upload to ${groupFolder.name}`
                  : 'Upload Files'
            }
            documentType={uploadDocumentType}
            groupId={uploadGroupId}
            completingMessage="Uploading…"
            allowFolder
            backgroundOnUpload
          />
        );
      })()}

      {/* Share modal */}
      <ShareModal
        open={!!sharingNode}
        onClose={() => setSharingNode(null)}
        title={sharingNode?.name ?? ''}
        shareType="document"
        currentShares={existingShares}
        currentGroupShares={groupShares}
        tenantShare={tenantShare}
        inheritedTenantPermission={
          sharingNode && isOrgLibraryFile(sharingNode) && sharingNode.created_by !== user?.user_id
            ? ResourcePermissionRole.READ
            : undefined
        }
        organisationName={organisationName}
        onAddPeople={handleAddPeople}
        onRemovePerson={handleRemovePerson}
        onUpdatePermission={handleUpdatePermission}
        onRemoveGroupShare={handleRemoveGroupShare}
        acceptablePermissions={[ResourcePermissionRole.READ]}
        defaultPermission={ResourcePermissionRole.READ}
        isOwner={sharingNode?.created_by === user?.user_id}
        canMove={sharingNode ? canMoveNode(sharingNode) : false}
        onMove={
          sharingNode && canMoveNode(sharingNode)
            ? () => {
                setMovingNode(sharingNode);
                setSharingNode(null);
              }
            : undefined
        }
        inOrganisationFolder={
          !!sharingNode &&
          (sharingNode.document_type === 'tenant_file' ||
            (sharingNode.document_type === 'wiki' &&
              (openFolderId === 'organisation' ||
                breadcrumb.some((b) => b.id === 'organisation' || b.folder_type === 'organisation'))))
        }
        availableInGroupNames={(() => {
          if (!sharingNode || !(sharingNode.group_relation || sharingNode.group_id)) {
            return [];
          }
          const groupId = sharingNode.group_id ?? currentGroupId;
          const name =
            groupsList?.groups.find((g) => g.id === groupId)?.name ??
            (groupId === currentGroupId ? groupFolder?.name : undefined);
          return name ? [name] : [];
        })()}
      />

      <DocumentMoveModal
        open={!!movingNode}
        node={movingNode}
        groups={groupsList?.groups ?? []}
        organisationName="Organisation Folder"
        tenantName={organisationName}
        currentGroupId={currentGroupId}
        currentGroupName={groupFolder?.name ?? null}
        canMoveToOrganisation={canShareWithOrg}
        isSubmitting={relocateMutation.isPending}
        onClose={() => setMovingNode(null)}
        onSubmit={async (destination) => {
          if (!movingNode) return;
          if (isUserFolder(movingNode)) {
            await relocateMutation.mutateAsync({ folderId: movingNode.id, destination });
            return;
          }
          if (!movingNode.document_id) return;
          await relocateMutation.mutateAsync({ documentId: movingNode.document_id, destination });
        }}
      />

      <DocumentMoveModal
        open={!!linkingNode}
        node={linkingNode}
        groups={groupsList?.groups ?? []}
        organisationName="Organisation Folder"
        tenantName={organisationName}
        currentGroupId={currentGroupId}
        currentGroupName={groupFolder?.name ?? null}
        canMoveToOrganisation={false}
        intent="link"
        isSubmitting={linkToGroupMutation.isPending}
        onClose={() => setLinkingNode(null)}
        onSubmit={async (destination) => {
          if (!linkingNode?.document_id || destination.kind !== 'group') return;
          await linkToGroupMutation.mutateAsync({
            documentId: linkingNode.document_id,
            groupId: destination.groupId
          });
        }}
      />

      <DocumentPickerModal
        open={addExistingPicker === 'link'}
        onClose={() => setAddExistingPicker(null)}
        title="Link from Organisation Folder"
        confirmLabel="Link"
        isLoading={linkOrgLibraryMutation.isPending}
        contentsReady={groupFilesReady}
        contentsError={groupFilesError ? 'Could not load files already in this group.' : null}
        onRetryContents={() => {
          void refetchGroupFiles();
        }}
        excludeDocumentIds={alreadyLinkedDocumentIds}
        rootFolderId="organisation"
        rootFolderLabel="Organisation Folder"
        filesOnly={false}
        allowedDocumentTypes={ORG_LIBRARY_DOCUMENT_TYPES}
        allowedFolderTypes={new Set(['sharepoint'])}
        onConfirm={(documentIds) => linkOrgLibraryMutation.mutate({ documentIds })}
      />
      <DocumentPickerModal
        open={addExistingPicker === 'move'}
        onClose={() => setAddExistingPicker(null)}
        title={
          groupFolder?.name?.trim() ? `Move from Document Hub to ${groupFolder.name.trim()}` : 'Move from Document Hub'
        }
        confirmLabel="Move"
        isLoading={moveToGroupMutation.isPending}
        contentsReady={groupFilesReady}
        contentsError={groupFilesError ? 'Could not load files already in this group.' : null}
        onRetryContents={() => {
          void refetchGroupFiles();
        }}
        disabledDocumentIds={alreadyOwnedDocumentIds}
        disabledDocumentReason={
          groupFolder?.name?.trim() ? `Already in ${groupFolder.name.trim()}` : 'Already in this group'
        }
        filesOnly={false}
        omitDrafts
        moveAccessWarning={{ tenantName: organisationName, groupName: groupFolder?.name }}
        onConfirm={(documentIds) => moveToGroupMutation.mutate({ documentIds })}
      />

      <ConfirmModal
        open={pendingStatutoryAction !== null}
        onClose={() => setPendingStatutoryAction(null)}
        onConfirm={handlePendingStatutoryConfirm}
        title={pendingStatutoryCopy?.title ?? 'Statutory document linked'}
        message={pendingStatutoryCopy?.message ?? ''}
        confirmText={pendingStatutoryCopy?.confirmText ?? 'Continue'}
        variant="warning"
      />
    </div>
  );

  if (hideTrail) {
    return (
      <div className="flex w-full flex-col gap-4">
        {folderContextBanners}
        <Panel className="min-h-[60vh] w-full">{browser}</Panel>
        {reserveUploadWidgetSpace && <div className="h-[80px] shrink-0" aria-hidden />}
      </div>
    );
  }

  return browser;
}
